import { IPromise } from 'angular';
import { Messages } from '../../codegen/Localize';
import { IDetailFetcher } from '../../ts/fetchers/IDetailFetcher';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { RequestHelper } from '../../ts/helpers/RequestHelper';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { MetricMapper } from '../../ts/mappers/MetricMapper';
import { MetricDefinition } from '../models/MetricDefinition';
import { IMetricInfo } from '../models/IMetricInfo';
import { Module } from '../../ts/Module';
import { AddTagsToMetricsRequest } from '../../ts/ServerApi/requests/AddTagsToMetricsRequest';
import { DeleteMetricsRequest } from '../../ts/ServerApi/requests/DeleteMetricsRequest';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { GetMetricActionsRequest } from '../../ts/ServerApi/requests/GetMetricActionsRequest';
import { GetMetricsRequest } from '../../ts/ServerApi/requests/GetMetricsRequest';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../ts/ServerApi/requests/MarkRecentlyViewedRequest';
import { MarkViewedRequest, ViewedObjectType } from '../../ts/ServerApi/requests/MarkViewedRequest';
import { MoveMetricsToProject } from '../../ts/ServerApi/requests/MoveMetricsToProjectRequest';
import { RemoveTagsFromMetricsRequest } from '../../ts/ServerApi/requests/RemoveTagsFromMetricsRequest';
import { RenameMetricRequest } from '../../ts/ServerApi/requests/RenameMetricRequest';
import { IMetricDescriptionParams, SetMetricDescriptionRequest } from '../../ts/ServerApi/requests/SetMetricDescriptionRequest';
import { SetMetricsOwnerRequest } from '../../ts/ServerApi/requests/SetMetricsOwnerRequest';
import { SetMetricsSuspendedStateRequest } from '../../ts/ServerApi/requests/SetMetricsSuspendedStateRequest';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { IMetricActions } from '../../ts/ServerApi/types/IMetricActions';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../../ts/services/ErrorNotifyingServer';
import { FetcherFactory } from '../../ts/services/FetcherFactory';
import { ServerService } from '../../ts/services/ServerService';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';

export class Metrics implements IListResource<IMetricInfo>, IFieldValuesResource, IActionResource<IMetricActions>, IDetailResource<IMetricInfo> {

  public static $inject = [
    '$q',
    'ErrorNotifyingServer',
    'FetcherFactory'
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<IMetricInfo>;
  private allQuery: ResourceQuery<IMetricInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;
  private detailFetcher: IDetailFetcher<IMetricInfo>;

  constructor(
    private $q: ng.IQService,
    private ErrorNotifyingServer: ErrorNotifyingServer,
    FetcherFactory: FetcherFactory
  ) {
    this.server = ErrorNotifyingServer.getServerService();
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetMetricsRequest, ServerApiMapper.metrics);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
    this.detailFetcher = FetcherFactory.getServerDetailFetcher('getMetric', MetricMapper.mapDetail);
  }

  public all(): ResourceQuery<IMetricInfo> {
    return this.allQuery;
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'metric');
  }

  public getById(id: string): IPromise<IMetricInfo> {
    return this.detailFetcher.fetchById(id);
  }

  // Actions

  public actions(id: string): IPromise<IMetricActions> {
    return this.server.sendRequest(new GetMetricActionsRequest(id));
  }

  public del(ids: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteMetricsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetMetricsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public setMetricDescription = async (args: IMetricDescriptionParams): Promise<IResult> => {
    return this.ErrorNotifyingServer
      .sendRequestAndDelay(new SetMetricDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  }

  public rename(id: string, name: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(new RenameMetricRequest(id, name));
  }

  public moveToProject(targetProjectId: string, metricIds: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(metricIds,
        (idsBlock) => new MoveMetricsToProject({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new AddTagsToMetricsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public removeTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new RemoveTagsFromMetricsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public static isMetricInfo(item: any): item is IMetricInfo {
    return item &&
           item.contentType &&
           item.contentType === MetricDefinition.contentType &&
           item.hasOwnProperty('connectedView');
  }

  public markRecentlyViewed(id: string): IPromise<IResult> {
    return this.server.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Metric
    }));
  }

  public markViewed(id: string): IPromise<IResult> {
    return this.server.sendRequest(new MarkViewedRequest({
      objectId: id,
      objectType: ViewedObjectType.Metric
    }));
  }

  public resumeMetrics(ids: string[]): IPromise<IResult> {
    return this.server.sendRequest(new SetMetricsSuspendedStateRequest(false, ids));
  }

  public toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: successMessage
      });
    }
  }
}

Module.instance().service('Metrics', Metrics);
